import { getAccessToken } from '@/lib/access-token-store'
import { refreshAccessToken } from '@/lib/api/session-refresh'
import { useAppDispatch, useAuth } from '@/store/hooks'
import { logout } from '@/store/slices/authSlice'
import { useEffect, useRef, useState } from 'react'

/**
 * Recupera a sessão ao abrir a aplicação.
 *
 * Com o access token só em memória, recarregar a página deixa a aplicação sem
 * credencial em mãos — o que sobrevive é o cookie httpOnly do refresh. Aqui a
 * sessão é reconstruída a partir dele antes de a interface pedir qualquer dado.
 *
 * Devolve `ready` para a árvore só renderizar depois: sem isso, dezenas de
 * telas disparariam requisições sem token, cada uma tomando um 401 e disputando
 * a mesma renovação.
 */
export function useSessionBootstrap(): { ready: boolean } {
    const dispatch = useAppDispatch()
    const { isAuthenticated } = useAuth()

    // Só a primeira montagem interessa: renovações posteriores são do
    // interceptor, e não devem segurar a interface.
    const startedRef = useRef(false)
    const [ready, setReady] = useState(
        () => !isAuthenticated || Boolean(getAccessToken())
    )

    useEffect(() => {
        if (startedRef.current || ready) return
        startedRef.current = true

        let active = true

        refreshAccessToken()
            .then(token => {
                if (!active) return

                // Sem token: refresh expirado, sessão revogada ou logout feito
                // em outra aba. A identidade guardada localmente não vale nada
                // sozinha.
                if (!token) dispatch(logout())
            })
            .finally(() => {
                if (active) setReady(true)
            })

        return () => {
            active = false
        }
    }, [ready, dispatch])

    return { ready }
}
